"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import { MdArrowForward, MdSearch } from "react-icons/md";

import styles from "./all-locations-directory.module.css";

export type AllLocationsDirectoryItem = {
  name: string;
  region: string;
  properties: string;
  from: string;
};

type AllLocationsDirectoryProps = {
  isGlobal: boolean;
  marketName: string;
  directory: AllLocationsDirectoryItem[];
  locationsHref: string;
};

export default function AllLocationsDirectory({
  isGlobal,
  marketName,
  directory,
  locationsHref,
}: AllLocationsDirectoryProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter logic: If searching, filter the whole array. If not, slice the top 12.
  const displayedDirectory = useMemo(() => {
    if (!searchQuery.trim()) {
      return directory.slice(0, 12);
    }

    const lowerQuery = searchQuery.toLowerCase();
    return directory.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerQuery) ||
        item.region.toLowerCase().includes(lowerQuery),
    );
  }, [searchQuery, directory]);

  return (
    <section className={styles.section}>
      {/* Header Area */}
      <div className={styles.sectionHeader}>
        <div className={styles.titleBlock}>
          <h2>
            {isGlobal
              ? "All locations worldwide"
              : `Toate locatiile din ${marketName}`}
          </h2>
          <span className={styles.totalCount}>
            {directory.length} {isGlobal ? "Destinations" : "Locații"}
          </span>
        </div>

        <Link href={locationsHref} className={styles.viewAllLink}>
          {isGlobal ? "Explore global directory" : "Vezi directorul complet"}
          <MdArrowForward aria-hidden="true" />
        </Link>
      </div>

      {/* Sleek Search Bar */}
      <div className={styles.searchWrapper}>
        <MdSearch className={styles.searchIcon} aria-hidden="true" />
        <input
          type="text"
          className={styles.searchInput}
          placeholder={
            isGlobal
              ? "Search by country or region..."
              : "Caută după oraș sau județ..."
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Directory Grid */}
      <div className={styles.directory}>
        {displayedDirectory.length > 0 ? (
          displayedDirectory.map((item) => (
            <Link
              key={`${item.name}-${item.region}`}
              href={locationsHref}
              className={styles.directoryRow}
            >
              <div className={styles.primaryInfo}>
                <span className={styles.itemName}>{item.name}</span>
                <span className={styles.itemRegion}>{item.region}</span>
              </div>

              {/* UPDATED: Metrics with Contextual Micro-Labels */}
              <div className={styles.metricsInfo}>
                <div className={styles.metricBlock}>
                  <span className={styles.metricLabel}>
                    {isGlobal ? "Properties" : "Proprietăți"}
                  </span>
                  <span className={styles.itemProperties}>
                    {item.properties}
                  </span>
                </div>

                <div className={styles.metricBlock}>
                  <span className={styles.metricLabel}>
                    {isGlobal ? "Starting at" : "Preț de la"}
                  </span>
                  <span className={styles.itemPrice}>{item.from}</span>
                </div>
              </div>

              <MdArrowForward className={styles.rowArrow} aria-hidden="true" />
            </Link>
          ))
        ) : (
          <div className={styles.emptyState}>
            <p>No locations found matching "{searchQuery}"</p>
          </div>
        )}
      </div>
    </section>
  );
}
